/**
 * Site resolution for the multi-tenant ScamDefense deployment.
 *
 * One codebase, one Vercel deploy, two public faces:
 *   - `umbrella` — the B2B2C platform brand on the apex (scamdefense.org).
 *     The "one platform, themed per partner" pitch.
 *   - `nasaa`    — the US state-regulator pitch (nasaa.scamdefense.org),
 *     i.e. the original states build (choropleth, per-state pages).
 *
 * Resolution order:
 *   1. `?site=nasaa|umbrella` query param — explicit override for local dev
 *      and for demoing either build on any URL (e.g. before the subdomain
 *      DNS is live). Persisted to sessionStorage so it survives SPA
 *      navigations within the tab, and clears when the tab closes.
 *   2. Hostname — `nasaa.*` → nasaa pitch; everything else → umbrella.
 *
 * Pointing nasaa.scamdefense.org at this same Vercel project (Domains tab)
 * is all the infra this needs — no separate deploy.
 */
export type SiteId = 'umbrella' | 'nasaa' | 'dcuc' | 'amba' | 'granguard' | 'qatar'

const OVERRIDE_KEY = 'sd_site'

function isSiteId(v: string | null): v is SiteId {
  return (
    v === 'umbrella' ||
    v === 'nasaa' ||
    v === 'dcuc' ||
    v === 'amba' ||
    v === 'granguard' ||
    v === 'qatar'
  )
}

export function resolveSite(): SiteId {
  if (typeof window === 'undefined') return 'umbrella'

  // `?site=` is an explicit override, persisted in sessionStorage so it sticks
  // across SPA navigation (needed to preview a multi-page subdomain build
  // locally). It clears when the tab closes; reset any time with `?site=umbrella`.
  const params = new URLSearchParams(window.location.search)
  const override = params.get('site')
  if (isSiteId(override)) {
    try {
      window.sessionStorage.setItem(OVERRIDE_KEY, override)
    } catch {
      /* sessionStorage unavailable; the param still applies this load */
    }
    return override
  }

  try {
    const stored = window.sessionStorage.getItem(OVERRIDE_KEY)
    if (isSiteId(stored)) return stored
  } catch {
    /* ignore */
  }

  const host = window.location.hostname.toLowerCase()
  if (host === 'nasaa.scamdefense.org' || host.startsWith('nasaa.')) {
    return 'nasaa'
  }
  // DCUC / defense credit union pitch (military-themed). `onguard.` is accepted
  // too in case that name is adopted later.
  if (host.startsWith('dcuc.') || host.startsWith('onguard.')) {
    return 'dcuc'
  }
  // AMBA — Association of Military Banks of America pitch (navy/gold, also
  // military-themed). amba.scamdefense.org.
  if (host.startsWith('amba.')) {
    return 'amba'
  }
  // GranGuard — the ungated consumer face aimed at older adults, the
  // later-life counterpart to the military dcuc/amba builds. Credit unions
  // hand this URL to members. granguard.scamdefense.org; `seniors.` is
  // accepted too in case that host is added later.
  if (host.startsWith('granguard.') || host.startsWith('seniors.')) {
    return 'granguard'
  }
  // Community and campaign partners co-brand the GranGuard face on their own
  // subdomain (garvey.scamdefense.org, beekeeper.granguard.org,
  // myerberg.granguard.org, lafayette.granguard.org); see lib/community-partners.ts.
  if (
    host.startsWith('garvey.') ||
    host.startsWith('beekeeper.') ||
    host.startsWith('myerberg.') ||
    host.startsWith('lafayette.') ||
    host.startsWith('howard.') ||
    host.startsWith('montgomery.')
  ) {
    return 'granguard'
  }
  // Qatar — the bank-branded Arabic/English demo face for the Gulf pitch.
  // demo.scamdefense.org is the shareable link (neutral, reusable: `?site=`
  // still overrides it); alkhaleeji./qatar./qa. are branded aliases.
  if (
    host.startsWith('demo.') ||
    host.startsWith('alkhaleeji.') ||
    host.startsWith('qatar.') ||
    host.startsWith('qa.')
  ) {
    return 'qatar'
  }
  return 'umbrella'
}

/**
 * Subdomains that are platform faces or infrastructure, NOT partner tenants.
 * A host like `firstcommand.scamdefense.org` is a provisioned member tenant;
 * `dcuc.` / `nasaa.` are handled by resolveSite() and must never be treated
 * as a `?tenant=` overlay. Keep in sync with the API reserved-slug denylist.
 */
const RESERVED_SUBDOMAINS = new Set([
  'www',
  'api',
  'app',
  'admin',
  'partners',
  'sponsors',
  'mail',
  'staging',
  'preview',
  'nasaa',
  'dcuc',
  'onguard',
  'amba',
  'granguard',
  'seniors',
  'alkhaleeji',
  'qatar',
  'qa',
  'demo',
  'garvey',
  'beekeeper',
  'myerberg',
  'lafayette',
  'howard',
  'montgomery',
])

/**
 * Derive a partner tenant slug from a `<slug>.scamdefense.org` host. Returns
 * null for the apex, www, platform faces, reserved infra subdomains, and any
 * non-scamdefense.org host (local dev, vercel.app previews). The web app
 * combines this with the `?tenant=` override (param wins) so provisioned
 * members render live on their own subdomain once the wildcard domain is set.
 *
 * Spec: docs/specs/partner-portal.md §6.
 */
export function resolveTenantSlugFromHost(): string | null {
  if (typeof window === 'undefined') return null
  const host = window.location.hostname.toLowerCase()
  if (!host.endsWith('.scamdefense.org')) return null
  const sub = host.slice(0, -'.scamdefense.org'.length)
  // Only a single-label subdomain is a tenant (no further dots).
  if (!sub || sub.includes('.')) return null
  if (RESERVED_SUBDOMAINS.has(sub)) return null
  return sub
}

/**
 * The tenant slug currently in effect: `?tenant=` (explicit, wins) or the
 * subdomain. This is the single source of truth for which partner the page is
 * rendering as, used both to theme the page (__root) and to attribute learner
 * analytics to the right tenant (track client). Null = apex / no tenant.
 */
export function resolveActiveTenantSlug(): string | null {
  if (typeof window === 'undefined') return null
  const params = new URLSearchParams(window.location.search)
  return params.get('tenant') ?? resolveTenantSlugFromHost()
}

/** External URL of the NASAA states pitch, used by the umbrella site. */
export const NASAA_SITE_URL = 'https://nasaa.scamdefense.org'

/** Where umbrella partnership / sales enquiries go. */
export const CONTACT_EMAIL = 'info@scamdefense.org'

/**
 * Which brand the visitor is actually looking at. Every face except
 * `granguard` is a ScamDefense surface; the GranGuard face carries the
 * consumer brand end to end (decision 2026-07-28), so shared consumer pages
 * (about, privacy, contact) must name it rather than the host.
 *
 * Use this instead of hardcoding "ScamDefense" in any page a non-umbrella
 * face can reach.
 */
export type SiteBrand = {
  name: string
  contactEmail: string
  supportEmail: string
  siteUrl: string
}

const SCAMDEFENSE_BRAND: SiteBrand = {
  name: 'ScamDefense',
  contactEmail: 'partnerships@scamdefense.org',
  supportEmail: 'hello@scamdefense.org',
  siteUrl: 'https://scamdefense.org',
}

const BRANDS: Record<SiteId, SiteBrand> = {
  umbrella: SCAMDEFENSE_BRAND,
  nasaa: SCAMDEFENSE_BRAND,
  dcuc: SCAMDEFENSE_BRAND,
  amba: SCAMDEFENSE_BRAND,
  granguard: {
    name: 'GranGuard',
    contactEmail: 'info@granguard.org',
    supportEmail: 'info@granguard.org',
    siteUrl: 'https://granguard.org',
  },
  // The Qatar face is bank-branded; the bank's own name/contacts live in
  // components/qatar/brand.ts. This entry only covers the shared pages
  // (privacy) that read the generic brand object.
  qatar: {
    name: 'GranGuard',
    contactEmail: 'info@granguard.org',
    supportEmail: 'info@granguard.org',
    siteUrl: 'https://granguard.org',
  },
}

export function siteBrand(site: SiteId = resolveSite()): SiteBrand {
  return BRANDS[site]
}

/**
 * Who the shared consumer pages are speaking to. Derived from the face rather
 * than a `!== 'nasaa'` negation, which silently swept every new face into the
 * military copy branch when `granguard` was added.
 */
export type SiteAudience = 'state' | 'military' | 'laterLife' | 'retail'

const AUDIENCES: Record<SiteId, SiteAudience> = {
  umbrella: 'state',
  nasaa: 'state',
  dcuc: 'military',
  amba: 'military',
  granguard: 'laterLife',
  // Retail bank customers of every age, not a later-life or military niche.
  qatar: 'retail',
}

export function siteAudience(site: SiteId = resolveSite()): SiteAudience {
  return AUDIENCES[site]
}
